import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { User, SyncedCalendarEvent, COLLECTION_NAMES } from '../types';

export type ItemType = 'milestone' | 'action_item' | 'meeting';

interface CalendarSettings {
  syncEnabled: boolean;
  calendarId: string;
  googleAccessToken?: string;
  googleRefreshToken?: string;
  googleTokenExpiresAt?: Timestamp | Date | string;
}

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

/**
 * Hook for managing Google Calendar sync functionality
 * Migrated from Supabase to Firestore
 */
export function useCalendarSync() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [syncedCount, setSyncedCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);

  // Load calendar settings from user document
  const loadSettings = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        const isConnected = userData.googleCalendarConnected || false;
        const tokenData = userData.googleCalendarToken;
        const settingsData = userData.googleCalendarSettings || {};

        if (isConnected && tokenData) {
          setSettings({
            syncEnabled: (settingsData as any)?.syncEnabled !== false, // Default to true
            calendarId: (settingsData as any)?.calendarId || 'primary',
            googleAccessToken: (tokenData as any)?.accessToken as string | undefined,
            googleRefreshToken: tokenData.refresh_token as string | undefined,
            googleTokenExpiresAt: tokenData.tokenExpiry
              ? new Date(tokenData.tokenExpiry as number)
              : undefined,
          });
        } else {
          setSettings(null);
        }
      } else {
        setSettings(null);
      }
    } catch (error) {
      console.error('Error loading calendar settings:', error);
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Load sync statistics
  const loadSyncStats = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      const syncEventsQuery = query(
        collection(db, COLLECTION_NAMES.SYNCED_CALENDAR_EVENTS),
        where('userId', '==', currentUser.uid),
        where('itemType', 'in', ['milestone', 'action_item']),
        orderBy('lastSyncedAt', 'desc'),
        limit(1)
      );

      const snapshot = await getDocs(syncEventsQuery);
      setSyncedCount(snapshot.size);

      if (!snapshot.empty) {
        const latest = snapshot.docs[0].data() as SyncedCalendarEvent;
        if (latest.lastSyncedAt) {
          const timestamp = latest.lastSyncedAt as Timestamp;
          setLastSyncTime(timestamp.toDate().toISOString());
        }
      }
    } catch (error) {
      console.error('Error loading sync stats:', error);
    }
  }, [currentUser]);

  // Toggle sync enabled/disabled
  const toggleSync = useCallback(async () => {
    if (!currentUser?.uid || !settings) return;

    try {
      const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        const currentSettings = userData.googleCalendarSettings || {};

        await updateDoc(userDocRef, {
          googleCalendarSettings: {
            ...currentSettings,
            syncEnabled: !settings.syncEnabled,
          },
        });

        setSettings({
          ...settings,
          syncEnabled: !settings.syncEnabled,
        });
      }
    } catch (error) {
      console.error('Error toggling sync:', error);
      throw error;
    }
  }, [currentUser, settings]);

  // Disconnect Google Calendar
  const disconnect = useCallback(async () => {
    if (!currentUser?.uid) return false;

    try {
      const userDocRef = doc(db, COLLECTION_NAMES.USERS, currentUser.uid);
      await updateDoc(userDocRef, {
        googleCalendarConnected: false,
        googleCalendarToken: null,
        googleCalendarSettings: null,
      });

      setSettings(null);
      return true;
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      return false;
    }
  }, [currentUser]);

  // Sync all items (calls cloud function)
  const syncAllItems = useCallback(async (): Promise<SyncResult> => {
    if (!currentUser?.uid || !settings?.syncEnabled) {
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: ['Calendar sync not enabled or configured'],
      };
    }

    setSyncing(true);

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const { db } = await import('../../../utils/init-firebase');
      
      const functions = getFunctions(db.app);
      const syncGoogleCalendarFunction = httpsCallable(functions, 'syncGoogleCalendar');
      
      const result = await syncGoogleCalendarFunction({ userId: currentUser.uid });
      const data = result.data as any;
      
      return {
        success: data.success || false,
        synced: data.synced || 0,
        failed: data.failed || 0,
        errors: data.errors || [],
      };
    } catch (error: any) {
      console.error('Error syncing calendar:', error);
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: [error?.message || 'Unknown error occurred'],
      };
    } finally {
      setSyncing(false);
    }
  }, [currentUser, settings]);

  // Get synced count
  const getSyncedCount = useCallback(async (): Promise<number> => {
    if (!currentUser?.uid) return 0;

    try {
      const syncEventsQuery = query(
        collection(db, COLLECTION_NAMES.SYNCED_CALENDAR_EVENTS),
        where('userId', '==', currentUser.uid),
        where('itemType', 'in', ['milestone', 'todo', 'action_item'])
      );

      const snapshot = await getDocs(syncEventsQuery);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting synced count:', error);
      return 0;
    }
  }, [currentUser]);

  useEffect(() => {
    loadSettings();
    loadSyncStats();
  }, [loadSettings, loadSyncStats]);

  return {
    loading,
    syncing,
    settings,
    syncedCount,
    lastSyncTime,
    loadSettings,
    toggleSync,
    disconnect,
    syncAllItems,
    getSyncedCount,
  };
}

